import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './bidSetting.css';
import SponsoredToolbar from './sponsoredToolbar';

const BidSetting = () => {
  const navigate = useNavigate();
  const [bidAmount, setBidAmount] = useState(114);
  const [selectedCompetitiveness, setSelectedCompetitiveness] = useState("Competitive");
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef(null);

  const competitivenessRanges = {
    "Less competitive": { min: 25, max: 75, color: "#ff6b6b" },
    "Competitive": { min: 75, max: 180, color: "#ffd93d" },
    "More competitive": { min: 180, max: 280, color: "#6bcf7f" },
    "Most competitive": { min: 280, max: 370, color: "#4ecdc4" }
  };

  const getCompetitivenessFromBid = (bid) => {
    for (const [level, range] of Object.entries(competitivenessRanges)) {
      if (bid >= range.min && bid < range.max) {
        return level;
      }
      if (level === "Most competitive" && bid >= range.min) {
        return level;
      }
    }
    return "Competitive";
  };

  const handleBidChange = (e) => {
    const newBid = Math.max(25, Math.min(370, parseInt(e.target.value) || 25));
    setBidAmount(newBid);
    setSelectedCompetitiveness(getCompetitivenessFromBid(newBid));
  };

  const handleCompetitivenessClick = (level) => {
    setSelectedCompetitiveness(level);
    const range = competitivenessRanges[level];
    const midPoint = Math.round((range.min + range.max) / 2);
    setBidAmount(midPoint);
  };

  const getSliderPosition = () => {
    const totalRange = 370 - 25;
    const currentPosition = bidAmount - 25;
    return (currentPosition / totalRange) * 100;
  };

  const getCurrentColor = () => {
    return competitivenessRanges[selectedCompetitiveness]?.color || "#ffd93d";
  };

  const handleSliderClick = (e) => {
    if (!sliderRef.current) return;
    
    const rect = sliderRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = (clickX / rect.width) * 100;
    const newBid = Math.round((percentage / 100) * (370 - 25) + 25);
    const clampedBid = Math.max(25, Math.min(370, newBid));
    
    setBidAmount(clampedBid);
    setSelectedCompetitiveness(getCompetitivenessFromBid(clampedBid));
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    handleSliderClick(e);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !sliderRef.current) return;
    
    const rect = sliderRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (clickX / rect.width) * 100));
    const newBid = Math.round((percentage / 100) * (370 - 25) + 25);
    const clampedBid = Math.max(25, Math.min(370, newBid));
    
    setBidAmount(clampedBid);
    setSelectedCompetitiveness(getCompetitivenessFromBid(clampedBid));
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  return (
    <div className="bid-setting-container">
      <SponsoredToolbar onlyHelp={true} />
      <div className="bid-setting-content">
        <h1 className="bid-setting-title">Set a bid to enroll</h1>

        <div className="bid-section">
          <h2 className="bid-section-title">Bid</h2>
          <p className="bid-description">
            What you'd pay for a booking from Sponsored Results, on top of the new patient booking fee. 
            Recommendations are based on winning bids in your market.
          </p>

          <div className="competitiveness-buttons">
            {Object.keys(competitivenessRanges).map((level) => (
              <button
                key={level}
                className={`competitiveness-button ${
                  selectedCompetitiveness === level ? 'selected' : ''
                }`}
                onClick={() => handleCompetitivenessClick(level)}
              >
                {selectedCompetitiveness === level && (
                  <span className="checkmark">✓</span>
                )}
                {level}
              </button>
            ))}
          </div>

          <div className="bid-input-section">
            <div className="bid-input-container">
              <span className="dollar-sign">$</span>
              <input
                type="number"
                value={bidAmount}
                onChange={handleBidChange}
                className="bid-input"
                min="25"
                max="370"
              />
            </div>

            <div className="slider-container">
              <div 
                ref={sliderRef}
                className="slider-track"
                onMouseDown={handleMouseDown}
              >
                <div 
                  className="slider-thumb"
                  style={{ 
                    left: `${getSliderPosition()}%`,
                    backgroundColor: getCurrentColor()
                  }}
                >
                  <div className="slider-label">
                    {selectedCompetitiveness}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="recommendation-box">
          <div className="recommendation-content">
            <div className="info-icon">ℹ</div>
            <div className="recommendation-text">
              We recommend you set bids in the 'competitive' range. Practices in the 'competitive' range or higher see 2X more bookings.
            </div>
          </div>
          <button className="cost-button">Understanding your cost</button>
        </div>

        <div className="terms-section">
          <ul className="terms-list">
            <li>Your bill is impressions-based, but we guarantee your average cost per booking will be at or below your bid</li>
            <li>Your bid does not include the Quidpro new client booking fee</li>
            <li>You can cancel your Sponsored Results at any time</li>
          </ul>
        </div>

        <div className="authorization-section">
          <p className="authorization-text">
            I authorize Quidpro to charge my account each month and acknowledge and agree to the terms of the{' '}
            <span className="ads-addendum">Ads Addendum</span>.
          </p>
        </div>

        <div className="footer-buttons">
          <button 
            className="back-button" 
            onClick={() => navigate(-1)}
          >
            Back
          </button>
          <button className="activate-button">Activate</button>
        </div>
      </div>
    </div>
  );
};

export default BidSetting;